<template>
  <Modal :style="computedWindowSize"
         id="sales-invoice-modal"
         :visible="visible"
         class="p-fluid positions-modal"
         @close="close">
    <template v-slot:header>
      <div class="p-dialog-title p-d-flex p-ai-center" v-if="itemData?.id">
        <div>
          <span v-if="isSalesInvoice">{{ $t('salesInvoiceEditModalTitle') }}</span>
          <span v-else-if="isPrepaymentInvoice">{{ $t('prepaymentInvoiceEditModalTitle') }}</span>
          {{ itemData.number }}
        </div>
        <ChangedAfterExportIcon :show="itemData.changed_after_export" />
      </div>
      <div class="p-dialog-title" v-else>
        <span v-if="isSalesInvoice">{{ $t('salesInvoiceCreateModalTitle') }}</span>
        <span v-else-if="isPrepaymentInvoice">{{ $t('prepaymentInvoiceCreateModalTitle') }}</span>
        {{ itemData.number }}
      </div>
    </template>
    <Spinner v-if="dataIsSending"/>
    <div v-else class="p-formgrid p-grid p-pt-3">
      <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-4 p-mb-5">
        <!--        :relatedCustomer="selectedContactPerson"-->
        <CustomerDropdown v-model="selectedCustomer"
                          ref="customerDropdown"
                          :disabled="customerDisabled || closedFinancialPeriodDataEditingDisabled"
                          :visible="visible"
                          :id="mode + '-invoice-modal-customer'"
                          :isWrong="submitted && !!selectedContactPerson && !selectedCustomer"
                          :required="!!selectedContactPerson"
                          :label="$t('Customer')"/>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-4 p-mb-5">
        <VehicleDropdown ref="vehicleDropdown"
                         v-model="selectedCar"
                         :disabled="carDisabled || closedFinancialPeriodDataEditingDisabled"
                         :visible="visible"
                         :id="mode + '-invoice-modal-vehicle'"
                         :customerId="selectedCustomer?.id ? +selectedCustomer.id : null"
                         :isForkliftTenant="$store.getters.isForkliftTenant"
                         :label="$store.getters.isForkliftTenant ? $t('Equipment') : $t('Car')"/>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-4 p-mb-5">
        <div class="p-float-label">
          <CustomDropdown id="invoice-state"
                          disabled
                          :class="{'p-invalid' : submitted && !selectedState}"
                          v-model="selectedState"
                          :options="InvoiceStates"
                          optionLabel="label"
                          :filter="false"
                          :showClear="false">
            <template #value="slotProps">
              <div v-if="slotProps.value">
                <span class="state-badge" :class="slotProps.value.color">{{ slotProps.value.label[$i18n.locale] }}</span>
              </div>
              <span v-else style="visibility: hidden">.</span>
            </template>
            <template #option="slotProps">
              <div>
                <span class="state-badge" :class="slotProps.option.color">{{ slotProps.option.label[$i18n.locale] }}</span>
              </div>
            </template>
          </CustomDropdown>
          <label for="invoice-state">{{$t('Status')}}<span class="warning-color">*</span></label>
        </div>
        <small class="p-invalid" v-if="submitted && !selectedState">{{ $t('Required field') }}</small>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-4 p-mb-5">
        <div class="p-float-label">
          <Calendar id="invoice-issued"
                    appendTo="self"
                    class="calendar-bottom"
                    v-model="issued"
                    :disabled="!$store.getters.isAccountantOrHigher || closedFinancialPeriodDataEditingDisabled"
                    @date-select="changeIssuedDate"
                    @blur="changeIssuedDate"
                    @show="toggleDropdownOverlayVisibility(true)"
                    @hide="toggleDropdownOverlayVisibility(false)"
                    :class="{'p-invalid' : (issued && !issuedDateIsValid) || submitted && !issued || (issued && payment_due && payment_due < issued)}"
                    :dateFormat="settings.dateFormat"
                    :showIcon="true"
                    :monthNavigator="true"
                    :yearNavigator="true"
                    :showButtonBar="true"
                    :minDate="computedMinDateValue"
                    :selectOtherMonths="true"
                    autocomplete="off"/>
          <label for="invoice-issued">{{ $t('issuedLabel') }}<span class="warning-color">*</span></label>
        </div>
        <small class="p-invalid" v-if="submitted && !issued">{{ $t('Required field') }}</small>
        <small class="p-invalid" v-if="issued && !issuedDateIsValid">{{ $t('Invalid date format') }}</small>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-4 p-mb-5">
        <div class="p-float-label">
          <Calendar id="payment_due"
                    appendTo="self"
                    class="calendar-bottom"
                    @date-select="changePaymentDueDate"
                    @date-blur="changePaymentDueDate"
                    @show="toggleDropdownOverlayVisibility(true)"
                    @hide="toggleDropdownOverlayVisibility(false)"
                    v-model="payment_due"
                    :disabled="closedFinancialPeriodDataEditingDisabled"
                    :class="{'p-invalid' : (payment_due && !paymentDueDateIsValid) || submitted && !payment_due || (issued && payment_due && payment_due < issued)}"
                    :dateFormat="settings.dateFormat"
                    :showIcon="true"
                    :monthNavigator="true"
                    :yearNavigator="true"
                    :showButtonBar="true"
                    :yearRange="(new Date().getFullYear() - 5) + ':' + (new Date().getFullYear() + 1)"
                    :selectOtherMonths="true"
                    autocomplete="off"/>
          <label for="payment_due">{{ $t('paymentDueLabel') }}<span class="warning-color">*</span></label>
        </div>
        <small class="p-invalid" v-if="submitted && !payment_due">{{ $t('Required field') }}</small>
        <small class="p-invalid" v-if="payment_due && !paymentDueDateIsValid">{{ $t('Invalid date format') }}</small>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-4 p-mb-5">
        <div class="p-float-label">
          <InputNumber id="penalty"
                       :locale="computedNumberInputLocale"
                       v-model="itemData.penalty"
                       :disabled="closedFinancialPeriodDataEditingDisabled"
                       mode="decimal"
                       suffix="%"
                       :min="0"
                       :max="20"
                       :minFractionDigits="2"
                       :maxFractionDigits="2"
                       autocomplete="off"/>
          <label for="penalty">{{ $t('penaltyLabel') }}</label>
        </div>
      </div>

      <div class="p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-2 small-text" v-if="showClosedPeriodEditBanner || showIssuedDateClosedBanner">
        <MessageBar v-if="showClosedPeriodEditBanner"
                    :text="$t('invoiceClosedAccountingPeriodAlert')"
                    :accountingPeriodClosedError="true"
                    :fixedToTop="false">
          <template #button v-if="$store.getters.isAccountantOrHigher && !closedFinancialDataEditingEnabled && itemData?.id">
            <Button @click="closedFinancialDataEditingEnabled = true"
                    style="width: auto; color: #fff;"
                    icon="ti-pencil"
                    class="p-button-rounded p-button-sm p-button-outlined p-ml-2"
                    :label="$t('Edit')"/>
          </template>
        </MessageBar>

        <MessageBar v-else-if="showIssuedDateClosedBanner"
                    :text="$t('invoiceIssuedDateClosedAccountingPeriodAlert')"
                    :accountingPeriodClosedError="true"
                    :fixedToTop="false"/>
      </div>

      <ul class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 add-position-buttons background-shema-color">
        <li class="modal-input-switch" v-show="!$store.getters.isForkliftTenant">
          <div class="p-d-flex p-ai-center">
            <InputSwitch :id="'show-car-data-modal-' + mode"
                         class="switch-btn"
                         v-model="showCarData"
                         :disabled="editingIsDisabled"/>
          </div>
          <label :for="'show-car-data-modal-' + mode" class="switch-label pointer"
                 :class="{'no-pointer':editingIsDisabled}">
            {{ $t('showVehicleDataOnInvoiceLabel') }}
          </label>
        </li>
        <li class="modal-input-switch" v-if="accountingIntegrationConnected">
          <div class="p-d-flex p-ai-center">
            <InputSwitch :id="'export-invoice-switch-' + mode"
                         class="switch-btn"
                         :disabled="exportInvoiceTogglerIsDisabled || editingIsDisabled"
                         v-model="exportInvoiceToAccounting"/>
          </div>
          <label :for="'export-invoice-switch-' + mode"
                 class="switch-label pointer"
                 :class="{'no-pointer':exportInvoiceTogglerIsDisabled || editingIsDisabled}">
            {{ accountingExportLabel }}
          </label>
        </li>
      </ul>

      <div class="p-col-12 p-sm-12 p-md-12 p-lg-12 table-wrapper p-mb-5">
        <div v-if="computedDeletedPositionsExists">
          <a @click.prevent="showDeleted = !showDeleted" class="p-link">
            <i style="font-size: 0.9em" class="pi p-mr-1" :class="showDeleted ? 'pi-minus' :'pi-plus'"></i>
            <span>{{ showDeleted ? $t('lineItemHideDeleted') : $t('lineItemShowDeleted') }}</span>
          </a>
        </div>
        <table class="p-datatable p-datatable-striped p-datatable-sm p-datatable-hoverable-rows positions-table">
          <thead class="p-datatable-thead">
          <tr>
            <th class="table-title__icon"></th>
            <th class="table-title__name--first"
                v-if="accountingIntegrationConnected && exportInvoiceToAccounting">
              {{ $t('invoiceExportCodeLabel') }}
            </th>
            <!--            <th class="table-title__name&#45;&#45;first"></th>-->
            <th class="table-title__name--second">{{ $t('Code') }}</th>
            <th class="table-title__name--last">{{ $t('Name') }}</th>
            <th class="table-title__qty">{{ $t('Qty') }}</th>
            <th class="table-title__purchase" v-if="!isPrepaymentInvoice">{{ $t('purchasePriceShortLabel') }}</th>
            <th class="table-title__sell">{{ $t('sellingPriceShortLabel') }}</th>
            <th class="table-title__discount" v-show="showDiscountColumn">
              {{ $t('discountShortLabel') }}<span style="font-weight: 300">(%)</span>
            </th>
            <th class="table-title__sum">{{ $t('Sum') }}</th>
            <th class="table-title__tax">{{ $t('Tax') }}</th>
            <th class="table-title__total">{{ $t('Total') }}</th>
            <th class="table-title__request-state" v-show="showRequestStateHeader"></th>
            <th class="table-title__info" v-show="showHistoryButtonHeader"></th>
            <th class="table-title__info" v-show="thereIsFixedRequest"></th>
            <th v-show="thereIsReducedRequest" class="table-title__info">
              <span class="p-d-flex p-jc-center" v-tooltip.top="$t('lineItemReturnedSpecialOrderTooltip')">
                <i class="ti-back-left"/>
              </span>
            </th>
          </tr>
          </thead>
          <tbody class="p-datatable-tbody">
          <tr v-for="(position, index) of positions" :key="index">
            <template v-if="position.status === 1 || showDeleted">
              <!--                <td :class="{ 'new-item' : position.isNew, 'deleted-position-td':  position.status === 0 }">-->
              <!--                  <div class="p-d-flex p-ai-center">-->
              <!--                    <i :class="{-->
              <!--                      'ti-hummer':position.type === 1,-->
              <!--                      'ti-truck':position.type === 2,-->
              <!--                      'ti-package':position.type === 3,-->
              <!--                      'ti-pencil':position.type === 4,-->
              <!--                    }"/>-->
              <!--                  </div>-->
              <!--                </td>-->
              <td class="position-type-sort-cell"
                  :class="{ 'new-item' : position.isNew, 'deleted-position-td': position.status === 0 }">
                  <span class="position-static-icon">
                    <i :class="{
                      'ti-hummer': position.type === 1,
                      'ti-truck': position.type === 2,
                      'ti-package': position.type === 3,
                      'ti-pencil': position.type === 4,
                    }"/>
                  </span>
              </td>
              <td v-if="accountingIntegrationConnected && exportInvoiceToAccounting" :class="{'new-item' : position.isNew, 'deleted-position-td':  position.status === 0}">
                <CustomDropdown class="table-body__name-input--accounting-code"
                                data-key="code"
                                :class="{'p-invalid warning-background' : submitted && exportInvoiceToAccounting && !position.selectedAccountingCode }"
                                :disabled="position.status === 0 || exportInvoiceCodeInputIsDisabled"
                                v-model="position.selectedAccountingCode"
                                :options="accountingItems"
                                @change="changeAccountingCode(position)"
                                placeholder="$t('invoiceSelectExportCodePlaceholder')"
                                :filter="true"
                                :filterFields="['code', 'description']"
                                :showClear="false">
                  <template #value="slotProps">
                    <div v-if="slotProps.value">
                      <span v-if="slotProps.value.code">{{ slotProps.value.code }} - </span>{{ slotProps.value.description }}
                    </div>
                    <span v-else>{{ $t('invoiceSelectExportCodePlaceholder') }}</span>
                  </template>
                  <template #option="slotProps">
                    <div><span v-if="slotProps.option.code">{{ slotProps.option.code }} - </span>{{ slotProps.option.description }}</div>
                  </template>
                </CustomDropdown>
              </td>

              <template v-if="position.type === 1">
                <td :class="{ 'new-item' : position.isNew, 'deleted-position-td':  position.status === 0 }">
                  <InputText class="table-body__name-input--code"
                             v-if="position.selectedService"
                             disabled
                             v-model="position.selectedService.code"/>
                </td>
              </template>
              <template v-else-if="position.type === 2">
                <td :class="{ 'new-item' : position.isNew, 'deleted-position-td': position.status === 0, 'updated-item': position.reduced }">
                  <div v-tooltip.top="position.selectedSupplier?.company_name">
                    <InputText :placeholder="position.status === 0 || positionsEditingIsDisabled ? null : $t('Code')"
                               :disabled="position.status === 0 || positionsEditingIsDisabled"
                               v-model.trim="position.code"
                               maxlength="100"
                               @input="updatePosition(position, $event.target.value, 'code')"
                               class="table-body__name-input--code"
                               autocomplete="off"/>
                  </div>
                </td>
              </template>

              <template v-if="position.type === 3">
                <td :class="{ 'new-item' : position.isNew, 'deleted-position-td':  position.status === 0 }">
                  <InputText :placeholder="$t('Code')"
                             disabled v-model.trim="position.code"
                             maxlength="100"
                             @input="updatePosition(position, $event.target.value, 'code')"
                             class="table-body__name-input--code"
                             autocomplete="off"/>
                </td>
              </template>

              <template v-if="position.type === 4">
                <td :class="{ 'new-item' : position.isNew, 'deleted-position-td':  position.status === 0 }">
                  <InputText :placeholder="position.status === 0 || positionsEditingIsDisabled ? null : $t('Code')"
                             :disabled="position.status === 0 || positionsEditingIsDisabled"
                             v-model.trim="position.code"
                             maxlength="100"
                             @input="updatePosition(position, $event.target.value, 'code')"
                             class="table-body__name-input--code" autocomplete="off"/>
                </td>
              </template>
              <td :class="{ 'new-item' : position.isNew, 'deleted-position-td':  position.status === 0, 'inactive-position-td': position.status === 2, 'updated-item': position.reduced }">
                <InputText :placeholder="$t('Name')"
                           :disabled="(position.type === 1 && !position.selectedService) ||
                                        (position.type === 3 && !position.selectedWarehouseItem) ||
                                        position.status === 0 ||
                                        editingIsDisabled ||
                                        positionsEditingIsDisabled"
                           v-model.trim="position.name"
                           maxlength="100"
                           @input="updatePosition(position, $event.target.value, 'name')"
                           :class="{'p-invalid warning-background' : submitted && !position.name}"
                           class="table-body__name-input--name"
                           autocomplete="off"/>
              </td>
              <td :class="{ 'new-item' : position.isNew, 'deleted-position-td': position.status === 0 }">
                <!--                  Service-->
                <InputNumber
                    v-if="isSalesInvoice && position.type === 1"
                    :locale="computedNumberInputLocale"
                    disabled
                    :modelValue="position.invoice_qty"
                    class="table-body__qty-input"
                    mode="decimal"
                    :min="-99999"
                    :max="99999"
                    :showButtons="false"
                    :minFractionDigits="2"
                    :maxFractionDigits="2"
                    autocomplete="off"/>
                <!--                  Request-->
                <InputNumber v-else-if="position.type === 2"
                             :locale="computedNumberInputLocale"
                             :disabled="position.status === 0 || (!position.isNew && (position.state !== 1 && position.state !== 3)) || positionsEditingIsDisabled || position.fixed_qty"
                             @input="updatePosition(position, $event.value, 'qty', 99999, 0)"
                             @update:modelValue="updatePosition(position, $event, 'qty', 99999, 0)"
                             v-model="position.qty"
                             :inputClass="{'p-invalid warning-background' : submitted && !position.qty && position.status !== 0}"
                             class="table-body__qty-input"
                             :useGrouping="true"
                             :min="0"
                             :max="99999"
                             :showButtons="!positionsEditingIsDisabled && (position.isNew && position.status !== 0 || (!position.isNew && position.state <= 3 && position.status !== 0))"
                             autocomplete="off"/>
                <!--                  Warehouse item-->
                <InputNumber v-else-if="position.type === 3"
                             :locale="computedNumberInputLocale"
                             :disabled="position.status === 0 || editingIsDisabled || positionsEditingIsDisabled"
                             @input="updatePosition(position, $event.value, 'qty', 99999, 0)"
                             @update:modelValue="updatePosition(position, $event, 'qty', 99999, 0)"
                             v-model="position.qty"
                             :inputClass="{'p-invalid warning-background' : submitted && !position.qty}"
                             class="table-body__qty-input"
                             mode="decimal"
                             :min="1"
                             :max="99999"
                             :showButtons="position.status !== 0 && !editingIsDisabled && !positionsEditingIsDisabled"
                             :minFractionDigits="Number(position.is_decimal) ? 2 : 0"
                             :maxFractionDigits="Number(position.is_decimal) ? 2 : 0"
                             autocomplete="off"/>
                <InputNumber v-else
                             :locale="computedNumberInputLocale"
                             :disabled="position.status === 0 || editingIsDisabled || positionsEditingIsDisabled"
                             @input="updatePosition(position, $event.value, 'qty', 99999, -99999)"
                             @update:modelValue="updatePosition(position, $event, 'qty', 99999, -99999)"
                             v-model="position.qty"
                             :inputClass="{'p-invalid warning-background' : submitted && !position.qty}"
                             class="table-body__qty-input"
                             mode="decimal"
                             :min="-99999"
                             :max="99999"
                             :showButtons="position.status !== 0 && !editingIsDisabled && !positionsEditingIsDisabled"
                             :minFractionDigits="2"
                             :maxFractionDigits="2"
                             autocomplete="off"/>
              </td>
              <td :class="{ 'new-item' : position.isNew, 'deleted-position-td': position.status === 0, 'updated-item': position.reduced }">
                <InputNumber
                    v-if="isSalesInvoice && position.type === 1"
                    :locale="computedNumberInputLocale"
                    disabled
                    :modelValue="position.customer_purchase_price"
                    class="table-body__purchase-input"
                    :inputClass="{
                        'p-invalid warning-background': isPriceWarningVisible(position)
                      }"
                    mode="decimal"
                    :min="0"
                    :max="1000000"
                    :minFractionDigits="2"
                    :maxFractionDigits="4"
                    autocomplete="off"/>
                <InputNumber v-else-if="Number(position.type) !== 5"
                             @input="updatePosition(position, $event.value, 'purchase_price', 1000000, 0)"
                             :locale="computedNumberInputLocale"
                             :disabled="positionsEditingIsDisabled ||
                                          position.type === 1 ||
                                          position.type === 3 ||
                                          position.status === 0 ||
                                          (position.type === 2 && +position.fixed_qty > 0)"
                             v-model="position.purchase_price"
                             class="table-body__purchase-input"
                             :inputClass="{
                                 'p-invalid warning-background': isPriceWarningVisible(position)
                               }"
                             @focus="startPriceEditing(position)"
                             @blur="stopPriceEditing"
                             mode="decimal"
                             :min="0"
                             :max="1000000"
                             :minFractionDigits="2"
                             :maxFractionDigits="4"
                             autocomplete="off"/>
                <div v-else class="p-d-flex p-ai-center p-jc-center">
                  <i class="ti-minus"/>
                </div>
              </td>
              <td :class="{ 'new-item' : position.isNew, 'deleted-position-td': position.status === 0, 'updated-item': position.reduced }">
                <InputNumber v-if="isSalesInvoice && position.type === 1"
                             :locale="computedNumberInputLocale"
                             disabled
                             :modelValue="position.customer_sell_price"
                             :inputClass="{
                                 'p-invalid warning-background':
                                   isPriceWarningVisible(position)
                               }"
                             class="table-body__sell-input"
                             mode="decimal"
                             :min="0"
                             :max="1000000"
                             :minFractionDigits="2"
                             :maxFractionDigits="4"
                             autocomplete="off"/>
                <InputNumber v-else
                             :locale="computedNumberInputLocale"
                             :disabled="positionsEditingIsDisabled || (position.type === 3 && !position.selectedWarehouseItem)|| position.type === 1 || position.status === 0"
                             v-model="position.sell_price"
                             :inputClass="{
                                 'p-invalid warning-background': submitted && !position.sell_price ||
                                   isPriceWarningVisible(position)
                               }"
                             @focus="startPriceEditing(position)"
                             @blur="stopPriceEditing"
                             class="table-body__sell-input"
                             mode="decimal"
                             :min="0"
                             :max="1000000"
                             :minFractionDigits="2"
                             :maxFractionDigits="4"
                             autocomplete="off"/>
              </td>
              <td v-show="showDiscountColumn" :class="{ 'new-item' : position.isNew, 'deleted-position-td': position.status === 0, 'updated-item': position.reduced }">
                <InputNumber :locale="computedNumberInputLocale"
                             @input="updatePosition(position, $event.value, 'discount', 100, 0)"
                             :disabled="positionsEditingIsDisabled || position.status === 0"
                             v-model="position.discount"
                             class="table-body__discount-input"
                             mode="decimal"
                             :min="0"
                             :max="100"
                             :minFractionDigits="0"
                             :maxFractionDigits="2"
                             autocomplete="off"/>
              </td>
              <td :class="{ 'new-item' : position.isNew, 'deleted-position-td': position.status === 0, 'updated-item': position.reduced }">
                <InputNumber :locale="computedNumberInputLocale" v-model="position.sum" mode="decimal" class="table-body__sum-input" disabled :minFractionDigits="2" :maxFractionDigits="4"/>
              </td>
              <td :class="{ 'new-item' : position.isNew, 'deleted-position-td': position.status === 0, 'updated-item': position.reduced }">
                <CustomDropdown @change="changeTax(position, $event.value)"
                                class="table-body__tax-input"
                                data-key="id"
                                :class="{'p-invalid warning-background' : submitted && !position.selectedTax}"
                                :disabled="position.status === 0 || positionsEditingIsDisabled"
                                v-model="position.selectedTax"
                                :options="taxesData"
                                :filter="false"
                                optionLabel="value"
                                :showClear="false">
                  <template #value="slotProps">
                    <div v-if="slotProps.value">
                      <span>{{ formatDecimal(slotProps.value.value) }}%</span>
                    </div>
                    <span v-else style="visibility: hidden">.</span>
                  </template>
                  <template #option="slotProps">
                    <div>
                      <span>{{ formatDecimal(slotProps.option.value) }}% <span v-if="slotProps.option.name">({{ slotProps.option.name }})</span></span>
                    </div>
                  </template>
                </CustomDropdown>
              </td>
              <td :class="{ 'new-item' : position.isNew, 'deleted-position-td': position.status === 0, 'updated-item': position.reduced }">
                <InputNumber :locale="computedNumberInputLocale" v-model="position.total" class="table-body__total-input" mode="decimal" disabled :minFractionDigits="2" :maxFractionDigits="4"/>
              </td>
              <td v-show="showRequestStateHeader"
                  class="p-p-0"
                  :class="{ 'new-item' : position.isNew, 'deleted-position-td': position.status === 0, 'updated-item': position.reduced }">
                <RequestPositionStateButton v-if="position && position.type === 2"
                                            :isDisabled="positionsEditingIsDisabled"
                                            :position="position"
                                            :isSalesInvoiceModal="true"/>
              </td>
              <td v-show="showHistoryButtonHeader"
                  class="p-p-0"
                  :class="{ 'new-item' : position.isNew, 'deleted-position-td': position.status === 0, 'updated-item': position?.id === updatedPositionId || position.changedMultipleState, 'inactive-position-td': position.status === 2 }">
                <PositionHistoryButton :position="position"/>
              </td>
              <td class="p-p-0"
                  v-if="thereIsFixedRequest" :class="{ 'new-item' : position.isNew, 'deleted-position-td': position.status === 0, 'updated-item': position.reduced }">
                <PositionFixedQtyButton :position="position"/>
              </td>
              <td v-show="thereIsReducedRequest"
                  class="p-p-2"
                  :class="{
                    'new-item' : position.isNew,
                    'deleted-position-td': position.status === 0,
                    'updated-item': position.id === updatedPositionId || position.changedMultipleState || position.isRestored,
                    'inactive-position-td': position.status === 2,
                    'position-for-deletion-td': position.forDeletion,
                    'error-background-td': position.error
                  }">
                <div v-if="position.reduced_qty && +position.reduced_qty" v-tooltip.left="$t('lineItemReturnedQtyTooltip')">
                  <i class="ti-back-left"/>
                  <span style="font-size: 0.9em; position: relative; top: -1px; margin-left: 2px">{{ +position.reduced_qty }}</span>
                </div>
              </td>
            </template>
          </tr>

          <tr v-if="useConsumables && consumable">
            <!--            <td>-->
            <!--              <i class="ti-spray"></i>-->
            <!--            </td>-->
            <td class="position-type-sort-cell">
              <span class="position-static-icon">
                <i class="ti-spray"/>
              </span>
            </td>
            <td v-if="accountingIntegrationConnected && exportInvoiceToAccounting">
              <CustomDropdown :class="{'p-invalid warning-background' : submitted && exportInvoiceToAccounting && !consumable.selectedAccountingCode}"
                              class="table-body__name-input--accounting-code"
                              data-key="code"
                              :disabled="exportInvoiceCodeInputIsDisabled"
                              v-model="consumable.selectedAccountingCode"
                              :options="accountingItems"
                              @change="changeAccountingCode(consumable)"
                              :placeholder="$t('invoiceSelectExportCodePlaceholder')"
                              :filter="true"
                              :filterFields="['code', 'description']"
                              :showClear="false">
                <template #value="slotProps">
                  <div v-if="slotProps.value">
                    <span v-if="slotProps.value.code">{{ slotProps.value.code }} - </span>{{ slotProps.value.description }}
                  </div>
                  <span v-else>{{ $t('invoiceSelectExportCodePlaceholder') }}</span>
                </template>
                <template #option="slotProps">
                  <div><span v-if="slotProps.option.code">{{ slotProps.option.code }} - </span>{{ slotProps.option.description }}</div>
                </template>
              </CustomDropdown>
            </td>

            <td></td>

            <td>
              <InputText :class="{'p-invalid warning-background' : submitted && useConsumables && consumable && !consumable.name}"
                         :disabled="positionsEditingIsDisabled"
                         v-model.trim="consumable.name"
                         maxlength="100"
                         class="table-body__name-input--name"
                         autocomplete="off"/>
            </td>

            <td>
              <InputNumber :locale="computedNumberInputLocale"
                           v-model="consumable.qty"
                           class="table-body__qty-input"
                           mode="decimal"
                           disabled
                           :min="2"
                           :max="999"
                           :minFractionDigits="2"
                           :maxFractionDigits="2"
                           autocomplete="off"/>
            </td>

            <td></td>

            <td>
              <InputNumber v-model="consumable.sell_price"
                           :locale="computedNumberInputLocale"
                           disabled
                           class="table-body__sell-input"
                           mode="decimal"
                           :min="0"
                           :minFractionDigits="2"
                           :maxFractionDigits="4"
                           autocomplete="off"/>
            </td>
            <td v-show="showDiscountColumn">
              <InputNumber :locale="computedNumberInputLocale"
                           :disabled="positionsEditingIsDisabled"
                           @input="updatePosition(consumable, $event.value, 'discount', 100, 0)"
                           v-model="consumable.discount"
                           class="table-body__discount-input"
                           mode="decimal"
                           :min="0"
                           :max="100"
                           :minFractionDigits="0"
                           :maxFractionDigits="2"
                           autocomplete="off"/>
            </td>
            <td>
              <InputNumber :locale="computedNumberInputLocale" v-model="consumable.sum" class="table-body__sum-input" mode="decimal" disabled :minFractionDigits="2" :maxFractionDigits="4"/>
            </td>
            <td>
              <CustomDropdown @change="changeTax(consumable, $event.value)"
                              :disabled="positionsEditingIsDisabled"
                              class="table-body__tax-input"
                              data-key="id"
                              :class="{'p-invalid' : submitted && useConsumables && consumable && !consumable.selectedTax}"
                              v-model="consumable.selectedTax"
                              :options="taxesData"
                              :filter="false"
                              optionLabel="value"
                              :showClear="false">
                <template #value="slotProps">
                  <div v-if="slotProps.value">
                    <span>{{ formatDecimal(slotProps.value.value) }}%</span>
                  </div>
                  <span v-else style="visibility: hidden">.</span>
                </template>
                <template #option="slotProps">
                  <div>
                    <span>{{ formatDecimal(slotProps.option.value) }}% <span v-if="slotProps.option.name">({{ slotProps.option.name }})</span></span>
                  </div>
                </template>
              </CustomDropdown>
            </td>
            <td>
              <InputNumber :locale="computedNumberInputLocale" v-model="consumable.total" class="table-body__total-input" mode="decimal" disabled :minFractionDigits="2" :maxFractionDigits="4"/>
            </td>
            <td v-show="showRequestStateHeader"></td>
            <td v-show="showHistoryButtonHeader"></td>
            <td v-show="thereIsFixedRequest"></td>
            <td v-show="thereIsReducedRequest"></td>
          </tr>

          <tr v-for="(position, index) of prepaymentPositions"
              :key="'prepay-' + index"
              class="prepayment-row">
            <template v-if="position.status === 1 || showDeleted">
              <!--              <td :class="{ 'new-item' : position.isNew, 'deleted-position-td':  position.status === 0 }">-->
              <!--                &lt;!&ndash;                  <i :class="{'ti-hummer':position.type === 1, 'ti-truck':position.type === 2, 'ti-package':position.type === 3, 'ti-pencil':position.type === 4}"></i>&ndash;&gt;-->
              <!--                <div class="p-d-flex p-ai-center">-->
              <!--                  <i class="ti-wallet"/>-->
              <!--                </div>-->
              <!--              </td>-->
              <td class="position-type-sort-cell">
                <span class="position-static-icon">
                  <i class="ti-wallet"/>
                </span>
              </td>
              <td v-if="accountingIntegrationConnected && exportInvoiceToAccounting">
                <CustomDropdown class="table-body__name-input--accounting-code"
                                data-key="code"
                                :class="{'p-invalid warning-background' : submitted && exportInvoiceToAccounting && !position.selectedAccountingCode }"
                                :disabled="position.status === 0 || exportInvoiceCodeInputIsDisabled"
                                v-model="position.selectedAccountingCode"
                                :options="accountingItems"
                                @change="changeAccountingCode(position)"
                                placeholder="Select item"
                                :filter="true"
                                :filterFields="['code', 'description']"
                                :showClear="false">
                  <template #value="slotProps">
                    <div v-if="slotProps.value">
                      <span v-if="slotProps.value.code">{{ slotProps.value.code }} - </span>{{ slotProps.value.description }}
                    </div>
                    <span v-else>Select item</span>
                  </template>
                  <template #option="slotProps">
                    <div><span v-if="slotProps.option.code">{{ slotProps.option.code }} - </span>{{ slotProps.option.description }}</div>
                  </template>
                </CustomDropdown>
              </td>


              <td>
                <InputText :placeholder="position.status === 0 || positionsEditingIsDisabled ? null : $t('Code')"
                           v-model.trim="position.code"
                           maxlength="100"
                           :disabled="isSalesInvoice"
                           class="table-body__name-input--code" autocomplete="off"/>
              </td>

              <td>
                <InputText :placeholder="$t('Name')"
                           :disabled="isSalesInvoice"
                           v-model.trim="position.name"
                           maxlength="100"
                           :class="{'p-invalid warning-background' : submitted && !position.name}"
                           class="table-body__name-input--name"
                           autocomplete="off"/>
              </td>
              <td>
                <!--                  Request-->
                <InputNumber :locale="computedNumberInputLocale"
                             disabled
                             v-model="position.qty"
                             :inputClass="{'p-invalid warning-background' : submitted && !position.qty}"
                             class="table-body__qty-input"
                             mode="decimal"
                             :min="-99999"
                             :max="99999"
                             :showButtons="false"
                             :minFractionDigits="2"
                             :maxFractionDigits="2"
                             autocomplete="off"/>
              </td>
              <!--              <td v-if="isSalesInvoice"-->
              <!--                  :class="{ 'new-item' : position.isNew, 'deleted-position-td': position.status === 0, 'updated-item': position.reduced }">-->
              <!--                <InputNumber :locale="computedNumberInputLocale"-->
              <!--                             disabled-->
              <!--                             v-model="position.purchase_price" class="table-body__purchase-input"-->
              <!--                             :inputClass="{-->
              <!--                               'p-invalid warning-background': isPriceWarningVisible(position)-->
              <!--                             }"-->
              <!--                             mode="decimal"-->
              <!--                             :min="0"-->
              <!--                             :max="1000000"-->
              <!--                             :minFractionDigits="2"-->
              <!--                             :maxFractionDigits="4"-->
              <!--                             autocomplete="off"/>-->
              <!--              </td>-->

              <td v-if="isSalesInvoice"
                  :class="{
                    'new-item': position.isNew,
                    'deleted-position-td': position.status === 0,
                    'updated-item': position.reduced
                  }">
                <!--                <div class="table-cell-empty">-->
                <!--                  <i class="ti-minus"/>-->
                <!--                </div>-->
              </td>

              <td>
                <!--                  <InputNumber @input="updatePosition(position, $event.value, 'sell_price', 1000000, 0)"-->
                <InputNumber @input="onSellPriceInput(position, $event.value)"
                             :locale="computedNumberInputLocale"
                             :disabled="positionsEditingIsDisabled || isSalesInvoice || closedFinancialPeriodDataEditingDisabled"
                             v-model="position.sell_price"
                             :inputClass="{
                               'p-invalid warning-background': (submitted && !position.sell_price) ||
                                 isPriceWarningVisible(position)
                             }"
                             @focus="startPriceEditing(position)"
                             @blur="stopPriceEditing"
                             class="table-body__sell-input"
                             mode="decimal"
                             :min="0"
                             :max="1000000"
                             :minFractionDigits="2"
                             :maxFractionDigits="4"
                             autocomplete="off"/>
              </td>
              <td v-show="showDiscountColumn"></td>
              <td>
                <InputNumber :locale="computedNumberInputLocale"
                             v-model="position.sum"
                             mode="decimal"
                             class="table-body__sum-input"
                             disabled
                             :minFractionDigits="2"
                             :maxFractionDigits="4"/>
              </td>
              <td>
                <CustomDropdown @change="changeTax(position, $event.value)"
                                class="table-body__tax-input"
                                data-key="id"
                                :class="{'p-invalid warning-background' : submitted && !position.selectedTax}"
                                :disabled="positionsEditingIsDisabled || isSalesInvoice || closedFinancialPeriodDataEditingDisabled"
                                v-model="position.selectedTax"
                                :options="taxesData"
                                :filter="false"
                                optionLabel="value"
                                :showClear="false">
                  <template #value="slotProps">
                    <div v-if="slotProps.value">
                      <span>{{ formatDecimal(slotProps.value.value) }}%</span>
                    </div>
                    <span v-else style="visibility: hidden">.</span>
                  </template>
                  <template #option="slotProps">
                    <div>
                      <span>{{ formatDecimal(slotProps.option.value) }}% <span v-if="slotProps.option.name">({{ slotProps.option.name }})</span></span>
                    </div>
                  </template>
                </CustomDropdown>
              </td>
              <td>
                <InputNumber :locale="computedNumberInputLocale"
                             v-model="position.total"
                             class="table-body__total-input"
                             mode="decimal"
                             disabled
                             :minFractionDigits="2"
                             :maxFractionDigits="4"/>
              </td>

              <td v-show="showRequestStateHeader"></td>

              <td v-show="showHistoryButtonHeader">
                <PositionHistoryButton :position="position"/>
              </td>

              <td class="p-p-1" v-show="thereIsFixedRequest"></td>

              <td v-show="thereIsReducedRequest"></td>
            </template>
          </tr>

          <tr style="background-color: inherit!important;">
            <td style="padding-right: 10px; line-height: 1.5; text-align: right" colspan="15">
              <ul class="p-text-right p-d-flex p-flex-column p-align-end">
                <template v-if="isPrepaymentInvoice && itemData.orderSummary?.grand_total">
                  <li class="p-d-flex p-ai-center" v-if="isPrepaymentInvoice">
                    <div>{{ $t('Order Total') }}: </div>
                    <div class="table-summary__price">{{ formatCurrency(itemData.orderSummary?.grand_total) }}</div>
                  </li>
                  <li class="p-d-flex p-ai-center">

                    <div>{{ $t('Prepayment') }} (%): </div>
                    <div class="table-summary__price">{{ formatDecimal(prepaymentPercent) }}%</div>
                  </li>
                  <li v-if="!closedFinancialPeriodDataEditingDisabled">
                    <ul class="p-d-flex p-ai-center p-flex-wrap">
                      <li class="p-p-1" v-for="p in [25,50,75,100]" :key="p">
                        <Button class="p-button-sm prepayment-percent-btn"
                                :class="{
                                  'p-button-outlined': !isPrepaymentPercentActive(p),
                                }"
                                :label="p + '%'"
                                @click="setPrepaymentPercent(p)"/>
                      </li>
                    </ul>
                    <hr class="p-p-0 p-mt-2 p-mb-2">
                  </li>
                </template>
                <li class="p-d-flex p-ai-center">
                  <div>{{ $t('Sum without tax') }}: </div>
                  <div class="table-summary__price">{{ formatCurrency(summary.sum) }}</div>
                </li>
                <li class="p-d-flex p-ai-center">
                  <div>{{ $t('Tax') }}: </div>
                  <div class="table-summary__price">{{ formatCurrency(summary.tax) }}</div>
                </li>
                <li class="p-d-flex p-ai-center">
                  <div>{{$t('rounding')}}: </div>
                  <div class="table-summary__price">{{ formatCurrency(summary.adjustment) }}</div>
                </li>
                <li class="p-d-flex p-ai-center p-text-bold">
                  <div>{{ isSalesInvoice ? $t('Grand total') : $t('Prepayment Total') }}: </div>
                  <div class="table-summary__price">{{ formatCurrency(summary.grand_total) }}</div>
                </li>
              </ul>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-6 p-m-0">
        <div class="p-field p-mb-5">
          <LimitedCharsTextarea :submitted="submitted"
                                :rows="2"
                                :disabled="editingIsDisabled"
                                v-model="itemData.invoice_comment"
                                :maxChars="salesInvoiceCommentMaxChars"
                                :id="'invoice-modal-comment-on-invoice'"
                                :label="$t('invoiceCommentLabel')"/>
        </div>

        <div class="p-field p-mb-5">
          <LimitedCharsTextarea :submitted="submitted"
                                :rows="2"
                                :disabled="editingIsDisabled"
                                v-model="itemData.staff_comment"
                                :maxChars="salesInvoiceStaffCommentMaxChars"
                                :id="'invoice-modal-staff-comment'"
                                :label="$t('invoiceInternalInfoLabel')"/>
        </div>
        <div class="p-field p-mb-5" v-show="isSalesInvoice">
          <LimitedCharsTextarea :submitted="submitted"
                                :rows="2"
                                :disabled="editingIsDisabled"
                                v-model="itemData.recommendation"
                                :maxChars="salesInvoiceRecommendationMaxChars"
                                :id="'invoice-modal-recommendation'"
                                :label="$t('Recommendations')"/>
        </div>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-6">
        <div class="payments-table-wrapper">
          <table class="p-datatable p-datatable-striped p-datatable-sm p-datatable-hoverable-rows payments-table">
            <thead class="p-datatable-thead">
            <tr>
              <th>{{ $t('paymentMethodLabel') }}</th>
              <th style="width: 120px">{{ $t('Sum') }}</th>
              <th style="width: 5%">{{ $t('Date') }}</th>
              <th style="width: 30px" v-if="payments?.find(payment => payment.id)"></th>
              <th style="width: 57px"></th>
            </tr>
            </thead>
            <tbody class="p-datatable-tbody" v-if="payments?.length">
            <tr v-for="(payment, index) of payments" :key="index" :class="{'new-item': payment.isNew}">
              <td>
                <CustomDropdown v-model="payment.selectedType"
                                style="min-width: 140px"
                                :disabled="!payment.isNew"
                                :class="{'p-invalid' : submitted && !payment.selectedType}"
                                :options="paymentTypes"
                                optionLabel="label"
                                :preventFilter="true"
                                :filter="false"
                                :showClear="false">
                  <template #value="slotProps">
                    <div class="p-d-flex p-ai-center" v-if="slotProps.value">
                      <i v-if="slotProps.value.type === 1 || slotProps.value.type === 3 || slotProps.value.type === 5"
                         class="payment-method-icon"
                         :class="{
                            'ti-wallet': slotProps.value.type === 1,
                            'ti-credit-card': slotProps.value.type === 3,
                            // 'ti-world': slotProps.value.type === 5
                            'ti-exchange-vertical vg-icon-rotate-90': slotProps.value.type === 5
                        }"/>
                      <span>
                          {{ slotProps.value.label[$i18n.locale] }}
                          <span v-if="slotProps.value.cashBoxName">({{ slotProps.value.cashBoxName }})</span>
                        </span>
                    </div>
                    <span v-else style="visibility: hidden">.</span>
                  </template>
                  <template #option="slotProps">
                    <div class="p-d-flex p-ai-center" v-if="slotProps.option">
                      <i v-if="slotProps.option.type === 1 || slotProps.option.type === 3 || slotProps.option.type === 5"
                         class="payment-method-icon"
                         :class="{
                            'ti-wallet': slotProps.option.type === 1,
                            'ti-credit-card': slotProps.option.type === 3,
                            // 'ti-world': slotProps.option.type === 5
                            'ti-exchange-vertical vg-icon-rotate-90': slotProps.option.type === 5
                        }"/>
                      <span class="p-mr-1">
                          {{ slotProps.option.label[$i18n.locale] }}
                          <span v-if="slotProps.option.cashBoxName">({{ slotProps.option.cashBoxName }})</span>
                        </span>
                    </div>
                  </template>
                </CustomDropdown>
              </td>
              <td>
                <InputNumber :disabled="!payment.isNew"
                             :locale="computedNumberInputLocale"
                             style="min-width: 80px;"
                             @input="changePaymentValue($event.value, payment)"
                             :inputClass="{'p-invalid' : submitted && !payment.value}"
                             v-model="payment.value"
                             :useGrouping="true"
                             mode="decimal"
                             :minFractionDigits="2"
                             :maxFractionDigits="2">
                </InputNumber>
              </td>
              <td>
                <Calendar style="width: 165px;"
                          :locale="computedNumberInputLocale"
                          :disabled="!payment.isNew"
                          id="invoice-payment-date"
                          class="calendar-bottom"
                          v-model="payment.date"
                          @date-select="checkPaymentDateIsValid(payment)"
                          @show="toggleDropdownOverlayVisibility(true)"
                          @hide="toggleDropdownOverlayVisibility(false)"
                          :class="{'p-invalid' : (payment.date && !payment.dateIsValid) || (submitted && !payment.date)}"
                          :dateFormat="settings.dateFormat"
                          :showIcon="true"
                          :monthNavigator="true"
                          :yearNavigator="true"
                          :showButtonBar="true"
                          :showTime="true"
                          :maxDate="new Date()"
                          :stepMinute="10"
                          :yearRange="(new Date().getFullYear() - 5) + ':' + (new Date().getFullYear() + 1)"
                          :selectOtherMonths="true"
                          autocomplete="off"/>
                <small class="p-invalid" v-if="payment.date && !payment.dateIsValid">{{ $t('Invalid date format') }}</small>
              </td>
              <td v-if="payments.find(payment => payment.id)">
                  <span v-if="payment.id">
                    <a @click.prevent="togglePaymentHistoryOverlay($event, payment.id)" href="#" class="p-link table-link-icon">
                      <i class="ti-time"></i>
                    </a>
                    <OverlayPanel :ref="'mpaymenth' + payment.id">
                      <div>
                        <span v-if="payment.created_at"><b>{{ $t('Created') }}:</b>
                          {{ formatBy(payment.created_at, payment.creator) }}
                        </span>
                      </div>
                    </OverlayPanel>
                  </span>
              </td>
              <td>
                <div class="p-d-flex p-jc-end">
                  <DeleteButton :disabled="closedFinancialPeriodDataEditingDisabled" @click="onDeletePaymentClick(index, payment.id)"/>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <div class="p-d-flex p-ai-center p-jc-between p-flex-wrap">
          <div class="p-p-2 p-mt-1">{{ $t('Due') }}: {{ formatCurrency(unpaidAmount) }} / {{ formatCurrency(summary.grand_total) }}</div>
          <div class=" p-mt-1">
            <Button :disabled="closedFinancialPeriodDataEditingDisabled"
                    @click="addNewPayment"
                    class="p-button-text"
                    icon="pi pi-plus"
                    :label="$t('Add Payment')"/>
          </div>
        </div>
        <div v-if="itemData.file_path">
          <hr class="p-mb-3">
          <div class="p-d-flex p-jc-between p-ai-center p-flex-wrap">
            <div class="p-d-flex">
              <div v-if="itemData.sent_at" class="p-d-flex p-ai-center p-flex-wrap p-text-secondary">
                <i class="ti-email p-mr-2"/>
                <div> {{ $t('statusSent') }}: {{ formatBy(itemData.sent_at, itemData.sent_by) }}</div>
              </div>
            </div>
            <!--            <div>-->
            <DownloadPdfButton :item="itemData" :type="mode" :isSecondary="true"/>
            <!--            </div>-->
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <Button :label="$t('Cancel')"
              icon="pi pi-times"
              class="p-button-text"
              @click="close"/>
      <Button :label="$t('Save')"
              :loading="dataIsSending"
              :disabled="disableSaveButton"
              icon="pi pi-check"
              :class="settings.isDevMode && exportInvoiceToAccounting ? 'p-button-danger p-button-raised': 'p-button-text'"
              @click="saveItem"/>
    </template>
  </Modal>

  <SupplierModal :visible="supplierModal"
                 :modalLayer="2"
                 :item="supplier"
                 :taxes="taxes"
                 @close="closeSupplierModal"
                 @update-items="updateSupplier"/>

  <ConfirmDeleteModal :modalDataIsLoading="modalDataIsLoading"
                      :visible="confirmPaymentDeleteModal"
                      @close="closeConfirmPaymentDeleteModal"
                      @delete-item="deletePayment"/>

  <ConfirmModal :visible="confirmEditClosedFinancialPeriodDataModal"
                :text="$t('closedFinancialPeriodAlert') + ' ' + $t('confirm.proceed')"
                @close="closeConfirmEditClosedFinancialPeriodDataModal"
                @confirm="editClosedFinancialPeriodDataModal"/>
  <ConfirmModal :visible="confirmSellingPriceIsTooLowModal"
                :text="$t('sellingPriceLowerThanPurchaseAlert') + ' ' + $t('confirm.proceed')"
                @close="closeConfirmSellingPriceIsTooLowModal"
                @confirm="saveItemAnyway"/>
  <ConfirmModal :visible="overdueTaxModal"
                :showSpinner="modalDataIsLoading"
                :text="$t('oldTaxError') + ' ' + $t('confirm.proceed')"
                @close="closeOverdueTaxModal"
                @confirm="saveItemWithoutOverdueTaxCheck"/>
</template>

<script>
import httpClient from '@/services/http.services'
import SupplierModal from '@/pages/data_directory/components/SupplierModal'
import PaymentTypes from '@/translations/PaymentTypes'
import PositionHistoryButton from '@/components/PositionHistoryButton'
import RequestPositionStateButton from "@/pages/orders/components/RequestPositionStateButton";
import PositionFixedQtyButton from '@/components/PositionFixedQtyButton'
import modalWithPositionsMixins from "@/mixins/modalWithPositionsMixins";
import settings from "@/settings";
import MessageBar from "@/components/MessageBar";
import Modal from "@/components/Modal";
import DownloadPdfButton from "@/components/DownloadPdfButton.vue";
import CustomerDropdown from "@/components/CustomerDropdown.vue";
import VehicleDropdown from "@/components/VehicleDropdown.vue";
import ChangedAfterExportIcon from "@/components/ChangedAfterExportIcon.vue";

export default {
  mixins: [
    modalWithPositionsMixins,
  ],
  components: {
    Modal,
    SupplierModal,
    PositionHistoryButton,
    RequestPositionStateButton,
    PositionFixedQtyButton,
    MessageBar,
    DownloadPdfButton,
    CustomerDropdown,
    VehicleDropdown,
    ChangedAfterExportIcon
  },
  emits: ['close', 'update-items', 'update-order'],
  name: 'InvoiceModal',
  props: {
    mode: {  //'sales' | 'prepayment'
      type: [String, null],
      default: null
    },
  },
  data() {
    return {
      prepaymentPositions: [],
      lockPrepaymentRecalc: false,
      prepaymentPercent: 0,
      exportInvoiceToAccounting: false,
      exportInvoiceTogglerIsDisabled: false,
      accountingIntegrationConnected: false,
      accountingIntegrationName: null,
      accountingItems: [],
      accountingDefaults: {
        product: null,
        service: null,
        consumable: null,
      },
      disableDeletePaymentButtons: false,
      disableAddPaymentBtn: false,
      issuedDateIsValid: false,
      paymentDueDateIsValid: false,
      dataIsSending: false,
      confirmPaymentDeleteModal: false,
      unpaidAmount: 0,
      payments: [],
      issued: null,
      payment_due: null,
      paymentTypes: [],
      exportInvoiceCodeInputIsDisabled: false,
    }
  },
  watch: {
    item(value) {
      if (!value) return

      this.itemData = { ...value }
      this.exportInvoiceToAccounting = this.accountingIntegrationConnected && this.getInitialAccountingExportValue()

      this.positionsEditingIsDisabled = !!(this.isSalesInvoice || (this.isPrepaymentInvoice && !value.fromOrderPage))

      this.customerDisabled = true
      this.carDisabled = true

      this.selectedCustomer = value.customer ? { ...value.customer } : null
      this.selectedCar = value.car ? { ...value.car } : null


      if (value.issued) {
        this.issued = new Date(value.issued * 1000)

        const lockTs = this.canUsePeriodLock
            ? this.$store.getters.locationData?.financial_period_locked_until || null
            : null

        this.financialPeriodHasBeenClosed = !!lockTs && value.issued <= lockTs

        if (this.financialPeriodHasBeenClosed) {
          if (!this.$store.getters.isAccountantOrHigher) {
            this.exportInvoiceTogglerIsDisabled = true
          }

          // this.disableClosedFinancialPeriodDataEditing()
          this.closedFinancialDataEditingEnabled = false
        }
      } else {
        this.issued = new Date()
        this.financialPeriodHasBeenClosed = false
      }

      this.checkIssuedDateFinancialPeriodIsClosed()
      this.checkIssuedDateIsValid()

      if (value.payment_due) {
        this.payment_due = new Date(value.payment_due * 1000)
      } else {
        this.calculatePaymentDue()
      }

      this.checkPaymentDueDateIsValid()

      if (value.isNew) {
        this.showCarData = true
        this.itemData.penalty = this.$store.getters.locationData.late_fee_percent ? +this.$store.getters.locationData.late_fee_percent : 0
      } else {
        this.showCarData = !!value.show_car_data
        this.itemData.penalty = value.penalty ? +value.penalty : 0
      }

      if (value.order_consumable) {
        value.consumable = value.order_consumable
        value.order_consumable = null
      }

      if (this.isSalesInvoice) this.constructConsumable(value)

      if (this.consumable && this.accountingIntegrationConnected) {
        const consumableSelectedAccountingCode = this.findAccountingItem(this.consumable)
        this.consumable.selectedAccountingCode = consumableSelectedAccountingCode ?? null
        this.consumable.accounting_code = consumableSelectedAccountingCode?.code ?? null
      }

      if (value.state) {
        this.selectedState = this.InvoiceStates.find(state => state.num === value.state)
      } else {
        this.selectedState = this.InvoiceStates[0]  // State 'New'
      }

      let positions = []
      if (value.positions?.length) {
        positions = value.positions
      } else if (value.order_positions) {
        positions = value.order_positions
      }

      // console.log(value)
      this.prepaymentPositions = []
      if (value.prepaymentInvoicePositions?.length) {
        this.prepaymentPositions = value.prepaymentInvoicePositions.map(p => {
          const taxId = p.tax_id ?? p.tax?.id ?? null
          const taxValue = p.tax_value ?? p.tax?.value ?? null

          p.type = 5

          const selectedTax = p.isNew ? this.pickDefaultTax(p) : this.findSelectedTax(p)
          const selectedAccountingCode = this.accountingIntegrationConnected ? this.findAccountingItem(p) : null

          return {
            selectedAccountingCode: selectedAccountingCode,
            id: p.id,
            status: 1,
            type: p.type,
            accounting_code: p.accounting_code ?? null,
            code: p.code,
            name: p.name,
            qty: p.qty ? +p.qty : 0,
            purchase_price: 0,
            sell_price: p.sell_price ? (this.isSalesInvoice ? -p.sell_price : +p.sell_price) : 0,
            sum: p.sum ? (this.isSalesInvoice ? -p.sum : +p.sum) : 0,
            total: p.total ? (this.isSalesInvoice ? -p.total : +p.total) : 0,
            tax_sum: p.tax_sum ? (this.isSalesInvoice ? -p.tax_sum : +p.tax_sum) : 0,
            tax_id: taxId,
            tax_value: taxValue,
            tax: p.tax,
            selectedTax: selectedTax,
            prepayment_invoice_grand_total: p.prepayment_invoice_grand_total ?? p.prepaymentInvoice?.grand_total ?? null,
            prepayment_invoice_adjustment: p.prepayment_invoice_adjustment ?? p.prepaymentInvoice?.adjustment ?? null,
            created_at: p.created_at,
            creator: p.creator,
          }
        })
      }

      if (this.isPrepaymentInvoice && this.prepaymentPositions?.length) {
        if (value.isNewPrepaymentInvoice) {
          this.setPrepaymentPercent(value.defaultPrepaymentPercent ?? 100)
        } else {
          this.recalcPrepaymentPercentFromSellPrice()
        }
      }

      if (positions?.length) {
        let positionSorting = 1
        this.positions = positions.map(position => {
          const selectedService = position.type === 1 ? this.findSelectedService(position) : null
          const selectedTax = this.isPrepaymentInvoice && position.isNew ? this.pickDefaultTax(position) : this.findSelectedTax(position)

          const selectedSupplier = position.type === 2 ? this.findSelectedSupplier(position) : null
          const selectedWarehouseItem = position.type === 3 ? this.findSelectedWarehouseItem(position) : null

          const originalAccountingCode = position.accounting_code ?? null;
          const selectedAccountingCode = this.accountingIntegrationConnected ? this.findAccountingItem(position) : null
          if (position.discount && +position.discount !== 0) {
            this.showDiscountColumn = true
          }

          let purchasePrice = 0

          const hasPositionPurchasePrice =
              position.purchase_price !== null &&
              position.purchase_price !== undefined &&
              position.purchase_price !== ''

          if (hasPositionPurchasePrice) {
            if (Number(position.type) === 1) {
              purchasePrice = this.$store.getters.isAccountantOrHigher
                  ? Number(position.purchase_price)
                  : 0
            } else {
              purchasePrice = Number(position.purchase_price)
            }
          } else if (selectedService && position.isNew) {
            purchasePrice = Number(selectedService.purchase ?? 0)
          }

          const qty = Number(position.qty ?? 0)
          const sellPrice = Number(position.sell_price ?? 0)

          const hasInvoiceQty =
              position.customer_qty !== null &&
              position.customer_qty !== undefined &&
              Number.isFinite(Number(position.customer_qty))

          const invoiceQty = hasInvoiceQty
              ? Number(position.customer_qty)
              : null

          const useCustomerQty =
              this.isSalesInvoice &&
              Number(position.type) === 1 &&
              invoiceQty !== null &&
              invoiceQty !== 0

          const customerQty = useCustomerQty ? invoiceQty : qty

          const customerQtyFactor = customerQty !== 0 ? qty / customerQty : 1

          const sorting = position.sorting ?? (position.isNew ? positionSorting++ : null)
          return {
            selectedAccountingCode: selectedAccountingCode,
            accounting_code: position.accounting_code ?? null,
            originalAccountingCode: originalAccountingCode,

            isUpdated: !!position.isUpdated || position.accounting_code !== originalAccountingCode,
            id: position.id,
            order_id: position.order_id,
            isNew: !!position.isNew,
            reduced: false,
            sorting: sorting,
            type: position.type,
            name: position.name,
            code: position.code ?? null,
            // raw_code: position.code ? this.generateRawCode(position.code) : null,
            raw_code: position.raw_code ? position.raw_code : null,

            // qty: position.qty ? +position.qty : 0,
            qty: qty,

            customer_qty: invoiceQty,
            customer_qty_manual: Number(position.type) === 1 && hasInvoiceQty,

            invoice_qty: customerQty,

            is_decimal: Number(position.type) === 3 && Number(position.warehouseItem?.is_decimal) === 1 ? 1 : 0,
            fixed_qty: position.fixed_qty ? +position.fixed_qty : 0,
            // reduced_qty: position.reduced_qty && !position.isNew ? +position.reduced_qty : 0,

            reduced_qty: Number(position.reduced_qty ?? 0),

            // purchase_price: purchasePrice,
            // purchase_sum: position.purchase_sum ? +position.purchase_sum : 0,
            // sell_price: position.sell_price ? +position.sell_price : 0,
            purchase_price: purchasePrice,
            purchase_sum: position.purchase_sum ? +position.purchase_sum : 0,

            customer_purchase_price: purchasePrice * customerQtyFactor,
            sell_price: sellPrice,
            customer_sell_price: sellPrice * customerQtyFactor,

            sell_price_fixed: !!(position.sell_price && position.type === 2),
            discount: position.discount && +position.discount !== 0 ? +position.discount : null,
            discount_sum: position.discount_sum && +position.discount_sum !== 0 ? +position.discount_sum : 0,
            selectedTax: selectedTax,
            tax_id: position.tax_id ?? selectedTax?.id ?? null,
            tax_value: selectedTax?.value ? +selectedTax.value : 0,
            tax_sum: position.tax_sum ? +position.tax_sum : 0,
            sum: position.sum ? +position.sum : 0,
            total: position.total ? +position.total : 0,
            // updated: false,
            state: position.state ? +position.state : position.type === 2 ? 1 : null,
            status: position.status,
            // service: position.service ? position.service : null,
            service_id: position.service_id,
            selectedService: selectedService,
            warehouse_item_id: position.warehouse_item_id,
            selectedWarehouseItem: selectedWarehouseItem,
            master_id: position.master_id,
            // selectedMaster: selectedMaster,
            supplier_id: position.supplier_id,
            selectedSupplier: selectedSupplier,
            requestStateHistoryCount: Number(
                position.requestStateHistoryCount ?? position.request_state_history?.length ?? 0
            ),
            // history: position.history ? position.history : null,
            historyCount: position.historyCount ? position.historyCount : null,
            created_at: position.created_at ? position.created_at : null,
            creator: position.creator ? position.creator : null,
            deleted_at: position.deleted_at ? position.deleted_at : null,
            deletedBy: position.deletedBy ? position.deletedBy : null,
            originalCode: position.code ?? null,
            originalName: position.name,
            originalServiceId: position.service_id,
            originalPurchasePrice: position.purchase_price ? +position.purchase_price : 0,
            originalPurchaseSum: position.purchase_sum ? +position.purchase_sum : 0,
            originalSellPrice: position.sell_price ? +position.sell_price : 0,

            originalQty: position.qty ? +position.qty : 0,
            originalInvoiceQty: invoiceQty,

            originalMasterId: position.master_id,
            // originalMaster: selectedMaster,
            originalSupplierId: position.supplier_id,
            originalSupplier: selectedSupplier,
            originalWarehouseItemId: position.warehouse_item_id,
            originalWarehouseItem: selectedWarehouseItem,
            originalStatus: position.status,
            originalDiscount: position.discount && +position.discount !== 0 ? +position.discount : null,
            originalTaxValue: selectedTax?.value ? +selectedTax.value : 0,
          }
        })
      }

      value.payments?.forEach(payment => {
        const paymentDate = payment.date ? new Date(payment.date * 1000) : new Date()
        const paymentData = {
          id: payment.id,
          type: payment.type,
          value: this.formatDecimal(payment.sum, false),
          date: paymentDate,
          cashbox_id: payment.cashbox_id ? payment.cashbox_id : null,
          created_at: payment.created_at ? payment.created_at : null,
          creator: payment.creator ? payment.creator : null,
          // cashbox_name: payment.cashbox ? payment.cashbox.name : null,
          cashBoxName: payment.cashbox ? payment.cashbox.name : null,
          dateIsValid: this.checkDateIsValid(paymentDate)
        }

        if (payment.type === 3 || payment.type === 5) { // If TYPE_BANK_CARD or TYPE_BANK_TRANSFER
          paymentData.selectedType = PaymentTypes.find(p => p.type === payment.type)
        } else { // Else if CASHBOX payment
          paymentData.selectedType = {...PaymentTypes[0], cashBoxName: paymentData.cashBoxName }
        }
        this.payments.push(paymentData)
      })

      // this.positions.forEach(position => {
      //   this.calculatePositionSummary(position)
      // })

      this.updateSummary()
      this.calculateUnpaidAmount()
    },
    visible(value) {
      if (!value) {
        this.submitted = false
        this.issuedDateIsValid = false
        this.paymentDueDateIsValid = false

        this.positionsEditingIsDisabled = false

        this.exportInvoiceToAccounting = false
        this.exportInvoiceTogglerIsDisabled = false
        this.exportInvoiceCodeInputIsDisabled = false
        this.resetAccountingExportConfig()
      } else {
        this.loadAccountingExportConfig()
      }
    },
    '$store.state.accountingIntegration'(integration) {
      if (this.visible && !integration?.directions?.sales?.configured) {
        this.resetAccountingExportConfig()
      }
    },
    '$store.state.user.cashboxes'() {
      this.constructPaymentTypes()
    },
    '$store.state.secondLayerIsOpened'() {
      if (!this.$store.state.secondLayerIsOpened) {
        if (this.confirmPaymentDeleteModal) {
          this.closeConfirmPaymentDeleteModal()
        }
      }
    },
  },
  mounted() {
    this.constructPaymentTypes()
  },
  methods: {
    getInitialAccountingExportValue() {
      // if (settings.isDevMode) return false
      if (this.itemData?.isNew || this.itemData?.isNewPrepaymentInvoice) {
        return Number(
            this.$store.getters.locationData?.default_export_sales_invoice ?? 0
        ) === 1
      }

      const storedValue = this.itemData?.export_to_accounting

      if (storedValue !== undefined && storedValue !== null) {
        return storedValue === true || Number(storedValue) === 1
      }

      return false
    },
    resetAccountingExportConfig() {
      this.accountingIntegrationConnected = false
      this.accountingIntegrationName = null
      this.accountingItems = []
      this.accountingDefaults = {
        product: null,
        service: null,
        consumable: null,
      }
      this.exportInvoiceToAccounting = false
    },
    loadAccountingExportConfig() {
      this.resetAccountingExportConfig()

      const integration = this.$store.getters.accountingIntegration
      const config = integration?.directions?.sales
      if (!integration || !config?.configured) return

      this.accountingIntegrationConnected = true
      this.accountingIntegrationName = integration.provider_name || null
      this.accountingItems = Array.isArray(config.codes)
          ? config.codes.map(item => ({
            code: String(item?.code ?? ''),
            description: String(item?.description ?? ''),
          })).filter(item => item.code && item.description)
          : []
      this.accountingDefaults = {
        product: config.defaults?.product ?? null,
        service: config.defaults?.service ?? null,
        consumable: config.defaults?.consumable ?? null,
      }

      this.exportInvoiceToAccounting = this.getInitialAccountingExportValue()
      this.exportInvoiceTogglerIsDisabled = !!this.financialPeriodHasBeenClosed
          && !this.$store.getters.isAccountantOrHigher
      this.applyAccountingConfigToPositions()
    },
    applyAccountingConfigToPositions() {
      if (!this.accountingIntegrationConnected) return

      const applyToPosition = position => {
        if (!position) return

        const previousCode = position.accounting_code == null
            ? null
            : String(position.accounting_code)
        const selected = this.findAccountingItem(position)

        position.selectedAccountingCode = selected
        position.accounting_code = selected?.code ?? null

        if (!position.isNew && previousCode !== position.accounting_code) {
          position.isUpdated = true
        }
      }

      this.positions?.forEach(applyToPosition)
      this.prepaymentPositions?.forEach(applyToPosition)
      applyToPosition(this.consumable)
    },
    changeAccountingCode(position) {
      if (!position) return
      position.isUpdated = !position.isNew
      position.accounting_code = position.selectedAccountingCode?.code ?? null
    },
    isPrepaymentPercentActive(percent) {
      const current = Number(String(this.prepaymentPercent ?? 0).replace(',', '.'))
      const target = Number(percent)

      if (!Number.isFinite(current) || !Number.isFinite(target)) return false

      return Math.abs(current - target) < 0.01
    },
    // setPrepaymentPercent(percent) {
    //   const prepaymentPosition = this.prepaymentPositions?.[0]
    //   if (!prepaymentPosition || !this.itemData) return
    //
    //   if (percent === null || percent === undefined || percent === '') return
    //
    //   const p = Number(String(percent).replace(',', '.'))
    //   if (!Number.isFinite(p)) return
    //
    //   const safeP = Math.min(100, Math.max(0, p))
    //
    //   this.lockPrepaymentRecalc = true
    //   try {
    //     this.prepaymentPercent = safeP
    //
    //     const base = Number(this.itemData.orderSummary?.sum ?? 0) // без налога, как у тебя
    //     const prepay = Math.round((base * safeP / 100) * 100) / 100
    //
    //     this.updatePosition(prepaymentPosition, prepay, 'sell_price', 1000000, -1000000)
    //   } finally {
    //     this.lockPrepaymentRecalc = false
    //   }
    //
    //   this.calculateUnpaidAmount()
    // },
    setPrepaymentPercent(percent) {
      const prepaymentPosition = this.prepaymentPositions?.[0]
      if (!prepaymentPosition || !this.itemData) return

      if (percent === null || percent === undefined || percent === '') return

      const toNum = (value) => Number(String(value ?? 0).replace(',', '.')) || 0

      const money = (value) => {
        const rounded = Math.round((Number(value) || 0) * 100) / 100
        return Math.abs(rounded) < 0.005 ? 0 : rounded
      }

      const p = Number(String(percent).replace(',', '.'))
      if (!Number.isFinite(p)) return

      const safeP = Math.min(100, Math.max(0, p))

      this.lockPrepaymentRecalc = true

      try {
        this.prepaymentPercent = safeP

        const orderGrandTotal = toNum(this.itemData.orderSummary?.grand_total)

        if (orderGrandTotal <= 0) {
          this.updatePosition(prepaymentPosition, 0, 'sell_price', 1000000, -1000000)
          return
        }

        const taxValue = toNum(
            prepaymentPosition.selectedTax?.value ??
            prepaymentPosition.tax_value
        )

        const taxMultiplier = 1 + taxValue / 100

        const targetGross = safeP === 100
            ? money(orderGrandTotal)
            : money(orderGrandTotal * safeP / 100)

        const targetNet = taxMultiplier > 0
            ? Math.round((targetGross / taxMultiplier) * 10000) / 10000
            : targetGross

        this.updatePosition(
            prepaymentPosition,
            targetNet,
            'sell_price',
            1000000,
            -1000000
        )
      } finally {
        this.lockPrepaymentRecalc = false
      }

      this.calculateUnpaidAmount()
    },
    onSellPriceInput(position, value) {
      this.updatePosition(position, value, 'sell_price', 1000000, 0)
      // если это предоплатная позиция (у
      // тебя она positions[0], но можно проверить и иначе)
      if (!this.isPrepaymentInvoice) return

      const prepaymentPosition = this.prepaymentPositions?.[0]

      if (!prepaymentPosition) return
      if (this.lockPrepaymentRecalc) return
      this.recalcPrepaymentPercentFromSellPrice()
      this.calculateUnpaidAmount()
    },
    // recalcPrepaymentPercentFromSellPrice() {
    //   const prepaymentPosition = this.prepaymentPositions?.[0]
    //   const orderSum = Number(this.itemData?.orderSummary?.sum ?? 0)
    //   if (!prepaymentPosition || orderSum <= 0) {
    //     this.prepaymentPercent = 0
    //     return
    //   }
    //
    //   const sell = Number(String(prepaymentPosition.sell_price ?? 0).replace(',', '.'))
    //   const percent = (sell / orderSum) * 100
    //
    //   const rounded = Math.round(percent * 10000) / 10000
    //   this.prepaymentPercent = Math.min(100, Math.max(0, rounded))
    // },
    // recalcPrepaymentPercentFromSellPrice() {
    //   const prepaymentPosition = this.prepaymentPositions?.[0]
    //
    //   const toNum = (value) => Number(String(value ?? 0).replace(',', '.')) || 0
    //
    //   const orderGrandTotal = toNum(this.itemData?.orderSummary?.grand_total)
    //
    //   if (!prepaymentPosition || orderGrandTotal <= 0) {
    //     this.prepaymentPercent = 0
    //     return
    //   }
    //
    //   const prepaymentGrandTotal = toNum(this.summary?.grand_total)
    //
    //   const percent = (prepaymentGrandTotal / orderGrandTotal) * 100
    //
    //   const rounded = Math.round(percent * 10000) / 10000
    //   this.prepaymentPercent = Math.min(100, Math.max(0, rounded))
    // },
    recalcPrepaymentPercentFromSellPrice() {
      const prepaymentPosition = this.prepaymentPositions?.[0]

      const toNum = (value) => Number(String(value ?? 0).replace(',', '.')) || 0

      const orderGrandTotal = toNum(this.itemData?.orderSummary?.grand_total)

      if (!prepaymentPosition || orderGrandTotal <= 0) {
        this.prepaymentPercent = 0
        return
      }

      const prepaymentGrandTotal = toNum(this.summary?.grand_total)

      const percent = (prepaymentGrandTotal / orderGrandTotal) * 100

      const rounded = Math.round(percent * 10000) / 10000

      this.prepaymentPercent = Math.max(0, rounded)
    },
    generatePrepaymentInvoicePosition() {
      const position = this.prepaymentPositions?.[0]
      if (!position) return

      return {
        prepayment_invoice_id: this.itemData?.id,
        accounting_code: position.accounting_code ?? null,
        code: position.code,
        name: position.name,
        qty: position.qty,
        sell_price: position.sell_price,
        // discount: 0,
        // discount_sum: 0,
        tax_id: position.selectedTax?.id,
        tax_value: position.selectedTax?.value,
        tax_sum: position.tax_sum,
        sum: position.sum,
        total: position.total,
      }
    },
    generateSalesPrepaymentAccountingCodes() {
      if (!this.isSalesInvoice || !this.exportInvoiceToAccounting) return null

      return (this.prepaymentPositions ?? [])
          .filter(position => position?.id)
          .map(position => ({
            id: position.id,
            accounting_code: position.selectedAccountingCode?.code
                ?? position.accounting_code
                ?? null,
          }))
    },
    findAccountingItem(position) {
      const currentCode = position?.accounting_code == null
          ? null
          : String(position.accounting_code)
      const accountingItem = currentCode === null
          ? null
          : this.accountingItems.find(item => String(item.code) === currentCode)

      let selectedAccountingCode = null
      if (accountingItem) {
        selectedAccountingCode = accountingItem
      } else {
        const defaultAccountingItem = this.findDefaultAccountingItem(position)
        if (defaultAccountingItem) {
          position.accounting_code = defaultAccountingItem.code
          selectedAccountingCode = defaultAccountingItem
        }
      }

      return selectedAccountingCode
    },
    findDefaultAccountingItem(position) {
      let positionType = 'consumable'

      if (position.type === 1) {
        positionType = 'service'
      } else if (
          position.type === 2 ||
          position.type === 3 ||
          position.type === 4 ||
          position.type === 5
      ) {
        positionType = 'product'
      }

      const defaultCode = this.accountingDefaults[positionType]
      if (defaultCode == null) return null

      return this.accountingItems.find(item => String(item.code) === String(defaultCode)) ?? null
    },
    checkPaymentDateIsValid(payment) {
      if (!payment) return false
      payment.dateIsValid = this.checkDateIsValid(payment.date)
    },
    changeIssuedDate() {
      if (!this.issued) return false
      this.issued = this.calculateDateWithCurrentTime(this.issued)

      this.checkIssuedDateIsValid()
      this.calculatePaymentDue()
      this.checkIssuedDateFinancialPeriodIsClosed()
    },
    checkIssuedDateFinancialPeriodIsClosed() {
      const lockTs = this.canUsePeriodLock
          ? this.$store.getters.locationData?.financial_period_locked_until || null
          : null

      const issuedTs = this.issued instanceof Date ? Math.floor(this.issued.getTime() / 1000) : null

      this.issuedDateFinancialPeriodHasBeenClosed = !!lockTs && !!issuedTs && issuedTs <= lockTs

      this.disableSaveButton =
          !this.$store.getters.isAccountantOrHigher &&
          this.issuedDateFinancialPeriodHasBeenClosed &&
          !this.financialPeriodHasBeenClosed
    },
    changePaymentDueDate() {
      if (!this.payment_due) return false
      this.payment_due = this.calculateDayEndDate(this.payment_due)
      this.checkPaymentDueDateIsValid()
    },
    checkIssuedDateIsValid() {
      this.issuedDateIsValid = this.checkDateIsValid(this.issued)
    },
    checkPaymentDueDateIsValid() {
      this.paymentDueDateIsValid = this.checkDateIsValid(this.payment_due)
    },
    checkDateIsValid(date) {
      if (!date) return false
      return !!(date instanceof Date && !isNaN(date))
    },
    calculatePaymentDue() {
      if (!this.issued || !this.issuedDateIsValid) return false

      const DAY = 24 * 60 * 60 * 1000
      const loc = this.$store.getters.locationData || {}

      const isSet = (v) => v !== null && v !== undefined && v !== ''

      let days

      if (this.isPrepaymentInvoice) {
        days = isSet(loc.prepayment_invoice_due_days)
            ? Number(loc.prepayment_invoice_due_days)
            : 1
      } else {
        if (isSet(this.selectedCustomer?.days_to_pay)) {
          days = Number(this.selectedCustomer.days_to_pay)
        } else if (isSet(loc.sales_invoice_due_days)) {
          days = Number(loc.sales_invoice_due_days)
        } else {
          days = 1
        }
      }

      if (!Number.isFinite(days) || days < 0) days = 1

      this.payment_due = new Date(+this.issued + days * DAY)

      this.checkPaymentDueDateIsValid()
      this.changePaymentDueDate()
    },
    constructPaymentTypes() {
      this.paymentTypes = []
      if (this.$store.state.user?.cashboxes?.length) {
        this.$store.state.user.cashboxes.forEach(cashbox => {
          const newLabel = PaymentTypes[0]?.label
          const paymentMethod = {
            type: 1,
            label: newLabel,
            cashBoxName: cashbox.name,
            cashboxId: cashbox.id ? cashbox.id : null
          }
          this.paymentTypes.push(paymentMethod)
        })
        const notCashPayments = PaymentTypes.filter(payment => payment.type !== 1)
        notCashPayments?.forEach(payment => this.paymentTypes.push(payment))
      } else {
        this.paymentTypes = PaymentTypes.filter(payment => payment.type !== 1)
      }
    },
    changePaymentValue(value, payment) {
      value = value && !isNaN(value) ? +value : 0
      payment.value = this.formatDecimal(value, false)
      this.calculateUnpaidAmount()
    },
    addNewPayment() {
      const payment = {
        isNew: true,
        date: new Date(),
        value: this.formatDecimal(this.unpaidAmount, false),
        dateIsValid: true,
      }

      this.payments.push(payment)
      this.calculateUnpaidAmount()
    },
    onDeletePaymentClick(index, paymentId) {
      if (paymentId) {
        this.paymentToDeleteId = paymentId
        this.paymentToDeleteIndex = index
        this.confirmPaymentDeleteModal = true
        this.$store.commit('toggleSecondLayer', true)
      } else {
        this.payments.splice(index, 1)
        this.calculateUnpaidAmount()
      }
    },
    async deletePayment() {
      this.modalDataIsLoading = true
      this.disableDeletePaymentButtons = true
      const payload = {
        id: this.paymentToDeleteId,
        invoiceId: this.itemData.id,
      }

      try {
        const { status, data } = await httpClient.post('payment/delete', payload)
        if (status === 200 && data) {
          if (data.success) {
            if (data.invoice_state) {
              const invoiceState = data.invoice_state
              this.selectedState = this.InvoiceStates.find(state => state.num === invoiceState)
            }
            this.$toast.add({severity:'success', detail: this.$t('Data deleted'), life: this.settings.toastLife});
            this.payments.splice(this.paymentToDeleteIndex, 1)
            this.closeConfirmPaymentDeleteModal()
            this.$emit('update-items', this.itemData.id)
            // this.$emit('update-order', this.itemData.id)
            this.calculateUnpaidAmount()
            if (this.itemData.export_id || this.itemData.export_number) {
              this.itemData.changed_after_export = 1
            }

            if (this.itemData.order_id) {
              this.$emit('update-order', this.itemData.order_id) // Update orderData
              this.sendUpdateOrderWebSocket(this.itemData.order_id)
            }
          } else if (data.error) {
            this.$toast.add({severity:'error', summary: data.error.summary, detail: data.error.detail, life: this.settings.toastLife});
          } else {
            this.showUnknownError()
          }
        }
      } catch(err) {
        this.showError(err)
      } finally {
        this.modalDataIsLoading = false
        this.disableDeletePaymentButtons = false
      }
    },
    closeConfirmPaymentDeleteModal() {
      this.confirmPaymentDeleteModal = false
      this.$store.commit('toggleSecondLayer', false)
      this.paymentToDeleteId = this.paymentToDeleteIndex = null
    },
    calculateUnpaidAmount() {
      if (this.payments?.length) {
        const paymentsTotalSum = this.payments.reduce((sum, payment) => sum + (+payment.value), 0)
        const unpaidPaymentsAmount = this.summary.grand_total - paymentsTotalSum
        this.unpaidAmount = this.formatDecimal(unpaidPaymentsAmount, false)
      } else {
        this.unpaidAmount = this.formatDecimal(this.summary.grand_total, false)
      }
    },
    togglePaymentHistoryOverlay(event, id) {
      if (!event || !id) {
        return false
      }
      this.$refs['mpaymenth' + id.toString()].toggle(event);
    },
    updateItem() {
      const id = this.itemData?.order_id
      if (!id) return
      this.itemData.sent_at = new Date()
      this.$emit('update-order', id) // Update order page
    },
    async saveItem() {
      if (!this.itemData) return
      this.submitted = true

      if (!this.issued ||
          !this.payment_due ||
          this.payment_due < this.issued ||
          (this.issued && !this.issuedDateIsValid) ||
          (this.payment_due && !this.paymentDueDateIsValid) ||
          !this.selectedState ||
          (this.itemData.invoice_comment && this.itemData.invoice_comment.length > this.salesInvoiceCommentMaxChars) ||
          (this.itemData.staff_comment && this.itemData.staff_comment.length > this.salesInvoiceStaffCommentMaxChars) ||
          (this.itemData.recommendation && this.itemData.recommendation.length > this.salesInvoiceRecommendationMaxChars)) {
        return false
      }

      let wrongInputs = false
      let noAccountingCodeSelected = false
      let positionSellingPriceIsLow = false
      let nameIsEmpty = false
      let sellingPriceIsEmpty = false
      let qtyIsEmpty = false
      let noTaxSelected = false
      let noServiceSelected = false
      let noSupplierSelected = false
      let noStockItemSelected = false

      this.positions?.filter(position => position.status === 1)?.forEach(position => {
        if (this.accountingIntegrationConnected && this.exportInvoiceToAccounting && !position.selectedAccountingCode) {
          noAccountingCodeSelected = true
          wrongInputs = true
        } else if (position.type === 1 && !position.selectedService) {
          noServiceSelected = true
          wrongInputs = true
        } else if (position.type === 2 && !position.selectedSupplier) {
          noSupplierSelected = true
          wrongInputs = true
        } else if (position.type === 3 && !position.selectedWarehouseItem) {
          noStockItemSelected = true
          wrongInputs = true
        } else if (!position.name) {
          nameIsEmpty = true
          wrongInputs = true
        } else if (!position.sell_price) {
          sellingPriceIsEmpty = true
          wrongInputs = true
        } else if (!position.qty) {
          qtyIsEmpty = true
          wrongInputs = true
        } else if (!position.selectedTax) {
          noTaxSelected = true
          wrongInputs = true
        } else if (this.isPositionSellingPriceLowerThanPurchase(position) && !this.ignoreSellingPriceIsTooLow) {
          positionSellingPriceIsLow = true
        }
      })

      this.prepaymentPositions?.forEach(position => {
        if (this.accountingIntegrationConnected && this.exportInvoiceToAccounting && !position.selectedAccountingCode) {
          noAccountingCodeSelected = true
          wrongInputs = true
        } else if (!position.name) {
          nameIsEmpty = true
          wrongInputs = true
        } else if (!position.sell_price) {
          sellingPriceIsEmpty = true
          wrongInputs = true
        } else if (!position.qty) {
          qtyIsEmpty = true
          wrongInputs = true
        } else if (!position.selectedTax) {
          noTaxSelected = true
          wrongInputs = true
        } else if (this.isPositionSellingPriceLowerThanPurchase(position) && !this.ignoreSellingPriceIsTooLow) {
          positionSellingPriceIsLow = true
        }
      })

      if (this.isSalesInvoice &&
          this.useConsumables &&
          this.consumable &&
          this.accountingIntegrationConnected &&
          this.exportInvoiceToAccounting &&
          !this.consumable.selectedAccountingCode) {
        noAccountingCodeSelected = true
        wrongInputs = true
      }

      if (noAccountingCodeSelected) {
        this.$toast.add({severity: 'error', detail: this.$t('noAccountingCodeSelectedMsg'), life: settings.toastLife});
      } else if (noServiceSelected) {
        this.$toast.add({severity: 'error', detail: this.$t('lineItemNoServiceSelectedMsg'), life: settings.toastLife});
      } else if (noSupplierSelected) {
        this.$toast.add({severity: 'error', detail: this.$t('lineItemNoSupplierSelectedMsg'), life: settings.toastLife});
      } else if (noStockItemSelected) {
        this.$toast.add({severity: 'error', detail: this.$t('lineItemNoStockItemSelectedMsg'), life: settings.toastLife});
      } else if (nameIsEmpty) {
        this.$toast.add({severity: 'error', detail: this.$t('lineItemNameRequiredMsg'), life: settings.toastLife});
      } else if (qtyIsEmpty) {
        this.$toast.add({severity: 'error', detail: this.$t('lineItemQtyRequiredMsg'), life: settings.toastLife});
      } else if (sellingPriceIsEmpty) {
        this.$toast.add({severity: 'error', detail: this.$t('lineItemSellingPriceRequiredMsg'), life: settings.toastLife});
      } else if (noTaxSelected) {
        this.$toast.add({severity: 'error', detail: this.$t('lineItemTaxRequiredMsg'), life: settings.toastLife});
      }

      this.payments?.forEach(payment => {
        if (!payment.selectedType || !payment.value || !payment.dateIsValid) {
          wrongInputs = true
        }
      })

      if (wrongInputs) {
        return false
      }

      if (!this.ignoreOverdueTax && this.checkThereIsPositionWithOverdueTax()) return

      if (positionSellingPriceIsLow) {
        this.openConfirmSellingPriceIsTooLowModal()
        return false
      }

      this.dataIsSending = true
      this.disableSaveButton = true

      const payload = {
        id: this.itemData.id ? this.itemData.id : null,
        order_id: this.itemData.order_id ? this.itemData.order_id : null,
        user_id: this.selectedCustomer ? +this.selectedCustomer.id : null,
        car_id: this.selectedCar ? this.selectedCar.id : null,
        state: this.selectedState ? this.selectedState.num : null,
        penalty: this.itemData.penalty ? +this.itemData.penalty : null,
        show_car_data: this.showCarData,
        sum: this.formatDecimal(this.summary.sum, false),
        tax: this.formatDecimal(this.summary.tax, false),
        adjustment: this.formatDecimal(this.summary.adjustment, false),
        grand_total: this.formatDecimal(this.summary.grand_total, false),
        invoice_comment: this.itemData.invoice_comment ? this.itemData.invoice_comment : null,
        staff_comment: this.itemData.staff_comment ? this.itemData.staff_comment : null,
        use_consumables: this.useConsumables,
        recommendation: this.itemData.recommendation ?? null,
        exportInvoiceToAccounting: this.accountingIntegrationConnected && this.exportInvoiceToAccounting,
        updatedPositions: this.isSalesInvoice ? this.generateUpdatedPositionsData() : null,
        prepaymentInvoicePosition: this.isPrepaymentInvoice ? this.generatePrepaymentInvoicePosition() : null,
        prepaymentInvoicePositionsAccounting: this.generateSalesPrepaymentAccountingCodes(),
        issued: this.issued
            ? Math.floor(this.issued.getTime() / 1000)
            : Math.floor(Date.now() / 1000),
        payment_due: this.payment_due
            ? Math.floor(this.payment_due.getTime() / 1000)
            : Math.floor(Date.now() / 1000),
      }


      let cashboxTransactionDescriptionString = null
      if (this.selectedCar) {
        cashboxTransactionDescriptionString = this.formatCarLabel(this.selectedCar)
      }

      const newPayments = this.payments?.filter(payment => payment.isNew)
      if (newPayments?.length) {
        payload.newPayments = newPayments.map(payment => {
          return {
            type: payment.selectedType.type,
            sum: this.formatDecimal(payment.value, false),
            state: payment.selectedType.type === 5 ? 1 : 3,
            date: payment.date ? +new Date(payment.date / 1000) : null ,
            unpaid: payment.selectedType.type === 5 ? this.formatDecimal(payment.value, false) : 0,
            cashboxId: payment.selectedType.cashboxId ? payment.selectedType.cashboxId : null,
            description: cashboxTransactionDescriptionString && cashboxTransactionDescriptionString !== '' ? cashboxTransactionDescriptionString : null
          }
        })
      }

      if (this.useConsumables && this.consumable) {
        payload.consumable = {
          accounting_code: this.consumable.selectedAccountingCode?.code ?? null
        }
      }

      const endpoint = this.isSalesInvoice ? 'sales-invoice' : 'prepayment-invoice'

      if (this.itemData.id) {
        try {
          const { status, data } = await httpClient.post(`${endpoint}/update`, payload)
          if (status === 200 && data?.success) {
            this.$emit('update-items', this.itemData.id) // Update sales-invoices page
            // this.updateItem()
            this.$toast.add({severity:'success', detail: this.$t('Data updated'), life: this.settings.toastLife});
            this.close()

            if (this.exportInvoiceToAccounting) {
              if (data?.export_error_message) {
                this.$toast.add({
                  severity:'error',
                  summary: this.$t('invoiceExportErrorTitle'),
                  detail: data.export_error_message,
                  life: this.settings.toastLife * 2
                });
              }
            }

            if (this.itemData.order_id) {
              this.$emit('update-order', this.itemData.order_id) // Update orderData page
              this.sendUpdateOrderWebSocket(this.itemData.order_id)
            }
          } else if (data?.error) {
            if (settings.isDevMode) console.log(data.error)
            this.$toast.add({severity:'error', summary: data.error.summary, detail: data.error.detail, life: this.settings.toastLife});
          } else if (this.canUsePeriodLock && data?.financial_period_closed_due) {
            this.financialPeriodHasBeenClosed = true
          } else {
            this.showUnknownError()
          }
        } catch(err) {
          this.showError(err)
        } finally {
          this.disableSaveButton = false
          this.dataIsSending = false
          this.ignoreSellingPriceIsTooLow = false
        }
      } else {
        try {
          const { status, data } = await httpClient.post(`${endpoint}/create`, payload)
          if (status === 201 && data?.success) {
            this.itemData.id = data.id
            this.$emit('update-items', this.itemData.id) // Update sales-invoices page
            this.$toast.add({severity:'success', detail: this.$t('Data saved'), life: this.settings.toastLife});
            this.close()

            if (this.exportInvoiceToAccounting) {
              if (data?.export_error_message) {
                this.$toast.add({
                  severity:'error',
                  summary: this.$t('invoiceExportErrorTitle'),
                  detail: data.export_error_message,
                  life: this.settings.toastLife * 2
                });
              }
            }
            if (this.itemData.order_id) {
              this.$emit('update-order', this.itemData.order_id) // Update orderData page
              this.sendUpdateOrderWebSocket(this.itemData.order_id)
            }
          } else if (data?.error) {
            if (settings.isDevMode) console.log(data.error)
            this.$toast.add({severity:'error', summary: data.error.summary, detail: data.error.detail, life: this.settings.toastLife});
          } else if (data?.alreadyCreated) {
            this.$emit('update-items', this.itemData.id)
            this.$emit('update-order', data.orderId)
            this.$toast.add({severity:'warn', detail: this.$t('invoiceAlreadyCreatedMsg'), life: this.settings.toastLife});
            this.close()
          } else if (this.canUsePeriodLock && data?.financial_period_closed_due) {
            this.$store.commit('updatelocationDataVariable', 'financial_period_closed_due', data.financial_period_closed_due)
            this.financialPeriodHasBeenClosed = true
            this.openConfirmEditClosedFinancialPeriodDataModal()
            if (!this.$store.getters.isAccountantOrHigher) {
              this.disableClosedFinancialPeriodDataEditing()
            }
          } else {
            this.showUnknownError()
          }
        } catch(err) {
          this.showError(err)
        } finally {
          this.disableSaveButton = false
          this.dataIsSending = false
          this.ignoreSellingPriceIsTooLow = false
        }
      }
    },
  },
  computed: {
    accountingExportLabel() {
      return this.$t('exportInvoiceToAccountingLabel', {
        provider: this.accountingIntegrationName,
      })
    },
    showClosedPeriodEditBanner() {
      return this.canUsePeriodLock
          && !!this.financialPeriodHasBeenClosed
    },

    showIssuedDateClosedBanner() {
      return this.canUsePeriodLock
          && !!this.issuedDateFinancialPeriodHasBeenClosed
          && !this.financialPeriodHasBeenClosed
    },

    // closedFinancialPeriodDataEditingDisabled() {
    //   return this.$store.getters.canUse('period_lock') &&
    //       this.financialPeriodHasBeenClosed &&
    //       !this.closedFinancialDataEditingEnabled
    // },

    isSalesInvoice() {
      return this.mode === 'sales'
    },
    isPrepaymentInvoice() {
      return this.mode === 'prepayment'
    },
    computedMinDateValue() {
      const now = new Date();
      const nowYear = now.getFullYear()
      const nowDay = now.getDate()
      const nowMonth = now.getMonth()

      let firstDate = new Date()

      if (this.$store.getters.isAccountantOrHigher) {
        firstDate = new Date(nowYear - 1, nowMonth, nowDay)
      }

      return firstDate
    },
    computedWindowSize() {
      if (this.mode === 'prepayment') {
        if (this.$store.state.mobileLayout) {
          return {position: 'absolute', top: 0, width: '96%', marginTop: '10px'}
        } else if (!this.$store.state.mobileLayout && this.$store.state.tabletLayout) {
          // return {position: 'absolute', top: 0, width: '94%', maxWidth: this.exportInvoiceToAccounting ? '1400px' : '1200px', marginTop: '15px'}
          return {position: 'absolute', top: 0, width: '94%', maxWidth: '1400px', marginTop: '15px'}
        } else {
          // return {position: 'absolute', top: 0, width: '90%', maxWidth: this.exportInvoiceToAccounting ? '1400px' : '1200px'}
          return {position: 'absolute', top: 0, width: '90%', maxWidth: '1400px'}
        }
      } else {

        if (this.$store.state.mobileLayout) {
          return {position: 'absolute', top: 0, width: '96%', marginTop: '10px'}
        } else if (!this.$store.state.mobileLayout && this.$store.state.tabletLayout) {
          // return {position: 'absolute', top: 0, width: '94%', maxWidth: this.exportInvoiceToAccounting ? '1600px' : '1420px', marginTop: '15px'}
          return {position: 'absolute', top: 0, width: '94%', maxWidth: '1600px', marginTop: '15px'}
        } else {
          // return {position: 'absolute', top: 0, width: '90%', maxWidth: this.exportInvoiceToAccounting ? '1600px' : '1420px'}
          return {position: 'absolute', top: 0, width: '90%', maxWidth: '1600px'}
        }

      }

    },
  },

}
</script>

<style scoped lang="scss">
.payments-table-wrapper {
  overflow: auto;
  //padding-right: 4px;
  padding-bottom: 10px;
  .payments-table {
    border-collapse: collapse;
    width: 100%;
    tr {
      td {
        padding: 8px !important;
      }
    }
  }
}
.payment-method-icon {
  margin-right: 6px;
  opacity: 0.85;
}
</style>